import VerifiedOutlined from "@mui/icons-material/VerifiedOutlined";
import StorefrontOutlined from "@mui/icons-material/StorefrontOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation } from "@tanstack/react-query";
import { facilityApi } from "@bw/shared/api";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { useMyOrg } from "@/hooks/use-my-org";

export function ClaimPlacePage() {
  const { placeId } = useParams<{ placeId: string }>();
  const orgView = useMyOrg();
  const [evidence, setEvidence] = useState("");
  const claim = useMutation({
    mutationFn: () => {
      const targetPlaceId = placeId;
      const organization = orgView.org;
      const organizationId = organization?.org_id;
      if (!targetPlaceId || !organizationId) throw new Error("Choose an organization before claiming a place.");
      return facilityApi.claimPlace(targetPlaceId, organizationId, evidence.trim());
    },
  });

  if (orgView.kind === "loading") return <Box sx={{ py: 10, textAlign: "center" }}><CircularProgress /></Box>;
  if (!orgView.org) return <Alert severity="warning">Select or create your organization before claiming this place.</Alert>;

  return (
    <Box sx={{ maxWidth: 760, mx: "auto", py: { xs: 2, md: 4 }, px: 2 }}>
      <Stack spacing={2.5}>
        <Box><Typography variant="h4">Claim this place</Typography><Typography color="text.secondary" sx={{ mt: 0.5 }}>Connect a community-added badminton place to {orgView.org.business_name}.</Typography></Box>
        {claim.isSuccess ? (
          <Card variant="outlined" sx={{ borderRadius: 3 }}><CardContent sx={{ py: 5, textAlign: "center" }}><VerifiedOutlined color="success" sx={{ fontSize: 52 }} /><Typography variant="h5" sx={{ mt: 1 }}>Claim submitted</Typography><Typography color="text.secondary" sx={{ mt: 1 }}>The platform team will review your evidence. Nothing about the place, bookings, or payments changes until approval.</Typography></CardContent></Card>
        ) : (
          <Card variant="outlined" sx={{ borderRadius: 3 }}><CardContent><Stack spacing={2.5}>
            <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}><Box sx={{ display: "grid", placeItems: "center", width: 44, height: 44, borderRadius: 2, bgcolor: "action.hover" }}><StorefrontOutlined /></Box><Box><Typography variant="h6">{orgView.org.business_name}</Typography><Typography variant="body2" color="text.secondary">Current organization</Typography></Box></Stack>
            <Alert severity="info">Share evidence that this organization operates the place—such as the official website, lease contact, or a business email. Email-domain matching never approves a claim automatically.</Alert>
            <TextField label="Ownership evidence" multiline minRows={5} value={evidence} onChange={(event) => setEvidence(event.target.value)} helperText={`${evidence.trim().length}/20 minimum characters`} />
            {claim.isError ? <Alert severity="error">{claim.error instanceof Error ? claim.error.message : "The claim could not be submitted."}</Alert> : null}
            <Box><Button variant="contained" disabled={claim.isPending || evidence.trim().length < 20} onClick={() => claim.mutate()}>{claim.isPending ? "Submitting…" : "Submit ownership claim"}</Button></Box>
          </Stack></CardContent></Card>
        )}
      </Stack>
    </Box>
  );
}
